import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

import dayjs from 'dayjs';

import Configure from '../../../components/Configure';
import Detail from '../../../components/Detail';
import ArrowLeft from '../../../images/arrow-left.svg?react';
import ArrowRight from '../../../images/arrow-right.svg?react';
import { temperatureContext } from '../../../lib/context';
import { convertTemperature } from '../../../lib/convert';
import { predict_missed } from '../../../lib/predict';
import { useUpdateRecordNote } from '../api/mutations';
import { getRecordById } from '../api/recordsApi';
import { createDataChildren } from '../composables/recordMappers';
import LabelEditDialog from './LabelEditDialog';
import MobilePendingDrawer from './MobilePendingDrawer';
import MobileRecordDrawer from './MobileRecordDrawer';

const HtmlTooltip = styled(({ className, ...props }: any) => <Tooltip {...props} classes={{ popper: className }} />)(
    ({ theme }: any) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: '#f5f5f9',
            color: 'rgba(0, 0, 0, 0.87)',
            maxWidth: 260,
            fontSize: theme.typography.pxToRem(12),
            border: '1px solid #dadde9',
        },
    })
);

const ROW_HEIGHT_DESKTOP = 60;
const ROW_HEIGHT_MOBILE = 60;

const formatDateOnly = (value: any) => (value ? dayjs(value).format('YYYY-MM-DD') : '-');
const DEFAULT_LABELS = {}; // use this to avoid running into rerender loop
const NO_LABEL_IDS: string[] = [];

function RecordRow({
    row,
    isItemSelected,
    onClick,
    ro,
    tKey,
    isMobile = false,
    mobileDrawerVariant = 'records',
    onRowEdit = () => {},
    labelsById = DEFAULT_LABELS,
}: any) {
    const { t } = useTranslation(undefined, { keyPrefix: tKey });
    const tempUnit = useContext(temperatureContext);
    const isPendingDrawer = mobileDrawerVariant === 'pending';

    const [open, setOpen] = useState(false);
    const [bgColor, setBgColor] = useState(row.num % 2 ? '#FFFFFF' : '#F3F5F9');

    const [editNote, setEditNote] = useState(false);
    const noteInput = useRef<HTMLInputElement>(null);
    const updateNote = useUpdateRecordNote();

    const [details, setDetails] = useState([]);
    const [minTemp, setMinTemp] = useState(100);
    const [maxTemp, setMaxTemp] = useState(-100);

    const [labelDialogOpen, setLabelDialogOpen] = useState(false);

    const [metaData, setMetaData] = useState({});
    const [threshold, setThreshold] = useState({});
    const [interval, setInterval] = useState(10);
    const [notifyEmails, setNotifyEmails] = useState([]);

    const [switchPage, setSwitchPage] = useState(false);
    const [mobileTab, setMobileTab] = useState('info');
    const [mobilePage, setMobilePage] = useState(0);
    const [loading, setLoading] = useState(false);

    const columnPercents = {
        number: '12%',
        status: '18%',
        device: '24%',
        time: '34%',
    };
    const baseCellSx = { borderBottom: 'none' };
    const mobileEllipsis = isMobile ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : {};
    const cellSx = { ...baseCellSx, ...(isMobile ? mobileEllipsis : {}) };
    const timeCellSx = isMobile
        ? { ...baseCellSx, whiteSpace: 'normal', lineHeight: 1.2 }
        : { ...baseCellSx, whiteSpace: 'nowrap' };
    const deviceCellSx = isMobile
        ? { ...baseCellSx, whiteSpace: 'normal', wordBreak: 'break-all', lineHeight: 1.2, fontSize: '11px' }
        : { ...baseCellSx };
    const statusPaddingClasses = isMobile ? 'px-2 py-1' : 'w-[50px] px-1 py-[3px]';

    // Copy the record's config into local state, and drop any readings loaded for it
    // so the next expand fetches fresh ones.
    const syncFromRow = useCallback(() => {
        setMetaData(row.metaData || {});
        setThreshold(
            row.threshold || {
                upper_limit: row.upperLimit,
                lower_limit: row.lowerLimit,
                time_limit: row.timeLimit,
                type: row.thresholdType,
            }
        );
        setNotifyEmails(row.notifyEmails || []);
        setInterval(row.interval || 10);
        setDetails([]);
    }, [row]);

    // An open row shows what onExpand loaded, already converted to display units.
    // When the record's data changes under it (a refetch), leave that view alone:
    // collapsing it, or swapping in the raw row values, while someone is reading it
    // was #272. Catch up once the row closes instead.
    const openRef = useRef(open);
    openRef.current = open;
    const staleWhileOpen = useRef(false);

    useEffect(() => {
        if (openRef.current) {
            staleWhileOpen.current = true;
            return;
        }
        syncFromRow();
    }, [syncFromRow]);

    useEffect(() => {
        if (!open && staleWhileOpen.current) {
            staleWhileOpen.current = false;
            syncFromRow();
        }
    }, [open, syncFromRow]);

    // Only a different record starts the row over. Rows are keyed by device + index,
    // so in practice this runs on mount; it used to run on every new row object.
    useEffect(() => {
        setDetails([]);
        setOpen(false);
        setMobileTab('info');
        setMobilePage(0);
    }, [row.deviceId, row.index]);

    // Both come from the query cache, so a label edit anywhere shows here without a refetch.
    const labelIds: string[] = row.labelIds || NO_LABEL_IDS;
    const labelObjects = labelIds.map((id) => labelsById[id]).filter(Boolean);

    const onEditNote = () => {
        setEditNote(true);
    };

    // The new note shows at once: the mutation patches the cached record, which
    // re-derives `row`, and puts the old note back if the save fails.
    const onSaveNote = () => {
        updateNote.mutate({ deviceId: row.deviceId, index: row.index, note: noteInput.current?.value ?? '' });
        setEditNote(false);
    };

    const onSwitchPage = () => {
        setSwitchPage(!switchPage);
    };

    const onExpand = () => {
        if (isPendingDrawer) {
            setOpen(!open);
            return;
        }
        if (details.length === 0 && !open) {
            const now = dayjs();
            const timeOffset = now.utcOffset();
            setLoading(true);
            setOpen(!open);
            getRecordById(row.deviceId, row.index).then((res: any) => {
                const payload = res?.body?.data || res?.body || {};
                let temps = payload.temps || [];
                temps = predict_missed(temps);
                temps = temps.map((temp: any) => convertTemperature(temp, tempUnit));
                let minTempL = Math.min(...temps, 100);
                let maxTempL = Math.max(...temps, -100);
                setMetaData(payload.meta_data || {});
                let thresholdResponse = payload.threshold || {};
                thresholdResponse = {
                    ...thresholdResponse,
                    upper_limit: convertTemperature(thresholdResponse.upper_limit, tempUnit),
                    lower_limit: convertTemperature(thresholdResponse.lower_limit, tempUnit),
                    alert_start_time: thresholdResponse.alert_start_time
                        ? dayjs(thresholdResponse.alert_start_time).add(timeOffset, 'm')
                        : dayjs().startOf('day'),
                    alert_end_time: thresholdResponse.alert_end_time
                        ? dayjs(thresholdResponse.alert_end_time).add(timeOffset, 'm')
                        : dayjs().endOf('day'),
                };
                setThreshold(thresholdResponse);
                setInterval(payload.interval);
                setNotifyEmails(payload.notify_emails || []);
                const startTimeBase = row.startTime || payload.start_time;
                const intervalValue = row.interval || payload.interval || 0;
                setDetails(
                    temps.map((temp: any, index: any) =>
                        createDataChildren(
                            dayjs(startTimeBase)
                                .add(index * intervalValue, 's')
                                .format('YYYY-MM-DD HH:mm'),
                            temp
                        )
                    )
                );
                setMinTemp(minTempL);
                setMaxTemp(maxTempL);
                setLoading(false);
            });
        } else {
            setOpen(!open);
        }
    };

    return (
        <React.Fragment>
            <TableRow
                sx={{
                    '& > *': { borderBottom: 'unset', py: 0.5 },
                    backgroundColor: bgColor,
                    height: isMobile ? ROW_HEIGHT_MOBILE : ROW_HEIGHT_DESKTOP,
                    minHeight: isMobile ? ROW_HEIGHT_MOBILE : ROW_HEIGHT_DESKTOP,
                    width: isMobile ? '100%' : 'auto',
                }}
            >
                <TableCell padding='checkbox' sx={{ borderBottom: 'none', px: 0, textAlign: 'center' }}>
                    <Checkbox
                        size='small'
                        checked={isItemSelected}
                        onClick={(event: any) => onClick(event, row.id)}
                        inputProps={{
                            'aria-labelledby': `enhanced-table-checkbox-${row.id}`,
                        }}
                    />
                </TableCell>
                <TableCell
                    component='th'
                    scope='row'
                    align='left'
                    sx={{ ...baseCellSx, width: isMobile ? columnPercents.number : 'auto' }}
                >
                    {row.num}
                </TableCell>
                <TableCell align='left' sx={{ ...cellSx, width: isMobile ? columnPercents.status : 'auto' }}>
                    <div className='flex justify-start w-full'>
                        {row.status !== undefined ? (
                            row.status === true ? (
                                <HtmlTooltip
                                    title={
                                        <React.Fragment>
                                            <span className='text-[#757575]'>{t('failAlarm')}</span> <br />
                                            {row.thresholdType === 'continuous'
                                                ? t('continuous')
                                                : t('cumulative')}: {t('outof')} {row.lowerLimit}°C {t('to')}{' '}
                                            {row.upperLimit}°C {t('range')}
                                            {t('for')} {row.timeLimit / 60} {t('minutes')}
                                        </React.Fragment>
                                    }
                                    placement='top'
                                >
                                    <div
                                        className={`text-base text-center rounded-[10px] text-[#3F8EE8!important] bg-[#3F8EE829] ${statusPaddingClasses}`}
                                    >
                                        {t('pass')}
                                    </div>
                                </HtmlTooltip>
                            ) : (
                                <HtmlTooltip
                                    title={
                                        <React.Fragment>
                                            <span className='text-[#757575]'>{t('failAlarm')}</span> <br />
                                            {row.thresholdType === 'continuous'
                                                ? t('continuous')
                                                : t('cumulative')}: {t('outof')} {row.lowerLimit}°C {t('to')}{' '}
                                            {row.upperLimit}°C {t('range')}
                                            {t('for')} {row.timeLimit / 60} {t('minutes')}
                                        </React.Fragment>
                                    }
                                    placement='top'
                                >
                                    <div
                                        className={`text-base text-center rounded-[10px] text-[#FF3838!important] bg-[#FF383829] ${statusPaddingClasses}`}
                                    >
                                        {t('fail')}
                                    </div>
                                </HtmlTooltip>
                            )
                        ) : (
                            <></>
                        )}
                    </div>
                </TableCell>
                <TableCell align='left' sx={{ ...deviceCellSx, width: isMobile ? columnPercents.device : 'auto' }}>
                    {row.deviceId}
                </TableCell>
                <TableCell align='left' sx={{ ...timeCellSx, width: isMobile ? columnPercents.time : 'auto' }}>
                    {isMobile ? (
                        <>
                            {row.startTime && row.endTime
                                ? `${formatDateOnly(row.startTime)} ${t('to')} ${formatDateOnly(row.endTime)}`
                                : '- : -'}
                        </>
                    ) : row.startTime && row.endTime ? (
                        `${row.startTime} ${t('to')} ${row.endTime}`
                    ) : (
                        '- : -'
                    )}
                </TableCell>
                {!isMobile && (
                    <>
                        <TableCell align='left' sx={cellSx}>
                            {row.length}
                        </TableCell>
                        <TableCell align='left' sx={cellSx}>
                            {row.duration}
                        </TableCell>
                        <TableCell align='left' sx={{ borderBottom: 'none' }}>
                            {editNote ? (
                                <input
                                    ref={noteInput}
                                    defaultValue={row.note}
                                    className='w-[100px] h-[22px] text-sm mr-1'
                                />
                            ) : row.note ? (
                                row.note
                            ) : (
                                <span className='text-[#757575]'>{t('note')}</span>
                            )}
                            {editNote ? (
                                <Button
                                    variant='contained'
                                    sx={{ width: '100px', height: '25px' }}
                                    onClick={onSaveNote}
                                >
                                    {t('confirm')}
                                </Button>
                            ) : !ro ? (
                                <IconButton onClick={onEditNote}>
                                    <EditIcon sx={{ fontSize: '15px' }} />
                                </IconButton>
                            ) : null}
                        </TableCell>
                        <TableCell align='left' sx={cellSx}>
                            <div className='flex items-center justify-start gap-1'>
                                {labelObjects.length ? (
                                    <div className='flex flex-wrap gap-1'>
                                        {labelObjects.map((label: any, idx: any) => (
                                            <span
                                                key={`${row.id}-${labelIds[idx] || label.id || label.name}-${idx}`}
                                                className='inline-flex items-center px-2 py-1 text-sm font-normal text-[#3c3c3c]'
                                                style={{
                                                    backgroundColor: label.color || '#EEF2FF',
                                                    borderRadius: '8px',
                                                }}
                                            >
                                                {label.name || label.id}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <span
                                        className='rounded-md bg-[#cccccc] px-2 py-1'
                                        style={{
                                            borderRadius: '8px',
                                            color: '#3c3c3c',
                                            fontWeight: 400,
                                            fontSize: '14px',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {t('labelsEmpty')}
                                    </span>
                                )}
                                {!ro && (
                                    <IconButton onClick={() => setLabelDialogOpen(true)} size='small'>
                                        <EditIcon sx={{ fontSize: '15px' }} />
                                    </IconButton>
                                )}
                            </div>
                        </TableCell>
                    </>
                )}

                <TableCell sx={{ borderBottom: 'none', width: 56, textAlign: 'center', px: 0 }}>
                    <IconButton aria-label='expand row' size='small' onClick={onExpand} className='text-[#5f6368]'>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>

            <TableRow sx={{ '& > *': { borderBottom: 'unset' }, backgroundColor: bgColor }}>
                <TableCell sx={{ paddingBottom: 0, paddingTop: 0, borderBottom: 'none', px: 0 }} colSpan={10}>
                    <Collapse in={open} timeout='auto' unmountOnExit>
                        {isMobile ? (
                            mobileDrawerVariant === 'pending' ? (
                                <MobilePendingDrawer
                                    tKey={tKey}
                                    row={row}
                                    metaData={metaData}
                                    threshold={threshold}
                                    activeTab={mobileTab}
                                    onTabChange={setMobileTab}
                                    onEditRow={onRowEdit}
                                />
                            ) : (
                                <MobileRecordDrawer
                                    tKey={tKey}
                                    row={row}
                                    metaData={metaData}
                                    threshold={threshold}
                                    notifyEmails={notifyEmails}
                                    details={details}
                                    minTemp={minTemp}
                                    maxTemp={maxTemp}
                                    loading={loading}
                                    activeTab={mobileTab}
                                    onTabChange={setMobileTab}
                                    pageIndex={mobilePage}
                                    onPageChange={setMobilePage}
                                    onEditNote={onEditNote}
                                    onSaveNote={onSaveNote}
                                    editNote={editNote}
                                    noteInput={noteInput}
                                    intervalValue={interval}
                                    ro={ro}
                                    onEditRow={onRowEdit}
                                />
                            )
                        ) : (
                            <div className='min-h-[630px] h-auto flex flex-row flex-nowrap'>
                                {!isPendingDrawer && (
                                    <button className='switch' onClick={onSwitchPage}>
                                        <ArrowLeft />
                                    </button>
                                )}
                                <Box className='w-full flex m-1 flex-row items-start justify-around'>
                                    {loading ? (
                                        <CircularProgress />
                                    ) : isPendingDrawer ? (
                                        <Configure
                                            deviceId={row.deviceId}
                                            index={row.index}
                                            metaData={metaData}
                                            threshold={threshold}
                                            interval={interval}
                                            notifyEmails={notifyEmails}
                                            tKey={`${tKey}.Edit`}
                                            ro={ro}
                                        />
                                    ) : switchPage ? (
                                        <Configure
                                            deviceId={row.deviceId}
                                            index={row.index}
                                            metaData={metaData}
                                            threshold={threshold}
                                            interval={interval}
                                            notifyEmails={notifyEmails}
                                            tKey={`${tKey}.Edit`}
                                            ro={ro}
                                        />
                                    ) : (
                                        <Detail
                                            details={details}
                                            row={row}
                                            tKey={tKey}
                                            maxTemp={maxTemp}
                                            minTemp={minTemp}
                                            bgColor={bgColor}
                                            threshold={threshold}
                                        />
                                    )}
                                </Box>
                                {!isPendingDrawer && (
                                    <button className='switch' onClick={onSwitchPage}>
                                        <ArrowRight />
                                    </button>
                                )}
                            </div>
                        )}
                    </Collapse>
                </TableCell>
            </TableRow>

            <LabelEditDialog
                open={labelDialogOpen}
                onClose={() => setLabelDialogOpen(false)}
                tKey={tKey}
                deviceId={row.deviceId}
                index={row.index}
                currentLabelIds={labelIds}
            />
        </React.Fragment>
    );
}

export default RecordRow;
